import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ApolloLink } from '@apollo/client';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { NewUsergroupFactory, ServiceCategoryFactory, UsergroupFactory } from 'app/factories';
import { ApolloQueryResult } from 'app/graphql/apollo-query-result';
import {
    CompensationUsergroupQuery,
    LabelTypeEnum,
    PurchaseAndSendRedeemCodeMutation,
    PurchaseAndSendRedeemCodeMutationVariables,
    PurchaseRedeemCodeMutation,
    PurchaseRedeemCodeMutationVariables,
    StoreLabelMutation,
    StoreLabelMutationVariables,
    ToggleContractsOnUsergroupMutation,
    ToggleContractsOnUsergroupMutationVariables,
    UpdateBankAccountMutation,
    UpdateBankAccountMutationVariables,
    UsergroupInvoice,
    UsergroupInvoicesQueryVariables,
    UsergroupLabelsQuery,
    UsergroupProfileHeaderDataQuery,
    UsergroupQuery,
} from 'app/graphql/generated/graphql-types';
import { RedeemCodesQuery, RedeemCodesQueryVariables } from 'app/graphql/generated/types-usergroup';
import { GenericPaginatedQueryResult, QueryResult } from 'app/interfaces';
import { UsergroupContext } from 'app/services/usergroup';

@Injectable()
export class UsergroupProfileService {
    private serviceCategoryFactory = inject(ServiceCategoryFactory);
    private usergroupFactory = inject(UsergroupFactory);
    private newUsergroupFactory = inject(NewUsergroupFactory);
    private usergroupContext = inject(UsergroupContext);

    // Created as class fields (constructor injection context) rather than inline, since
    // toObservable() must run in an injection context. The hash filter replicates the old
    // ReplaySubject's behaviour of never emitting until a truthy hash is set.
    private usergroupHash$: Observable<string> = toObservable(this.usergroupContext.hash).pipe(
        filter((hash): hash is string => !!hash),
    );
    private usergroupExists$: Observable<boolean> = toObservable(this.usergroupContext.exists);

    getBusinessAccounts(usergroupId: string | Observable<string>): Observable<QueryResult<any>> {
        return this.usergroupFactory.getBusinessAccounts(usergroupId);
    }

    getCompensationAccount(usergroupId: string | Observable<string>): Observable<QueryResult<any>> {
        return this.usergroupFactory.getUsergroupCompensationAccount(usergroupId);
    }

    getCompensationUsergroup(usergroupHash: string): Observable<ApolloQueryResult<CompensationUsergroupQuery>> {
        return this.usergroupFactory.getCompensationUsergroup(usergroupHash);
    }

    getEmail(usergroupId: string | Observable<string>): Observable<QueryResult<any>> {
        return this.usergroupFactory.getEmail(usergroupId);
    }

    getIncomeInfo(usergroupId: string | Observable<string>): Observable<QueryResult<any>> {
        return this.usergroupFactory.getIncomeInfo(usergroupId);
    }

    getInvoices(
        variables: UsergroupInvoicesQueryVariables,
    ): Observable<GenericPaginatedQueryResult<UsergroupInvoice[]>> {
        return this.usergroupFactory.getUsergroupInvoices(variables);
    }

    getInvoiceTypes(): Observable<ApolloQueryResult<any>> {
        return this.usergroupFactory.getUsergroupInvoiceTypes(this.usergroupHash$);
    }

    getLabels(
        usergroupHash: string,
        type?: LabelTypeEnum,
        search?: string,
    ): Observable<ApolloQueryResult<UsergroupLabelsQuery>> {
        return this.usergroupFactory.getLabels({ usergroupId: usergroupHash, type, search });
    }

    getLimits(usergroupId: string | Observable<string>): Observable<QueryResult<any>> {
        return this.usergroupFactory.getUsergroupLimits(usergroupId);
    }

    getVat(countryAbbreviation: string): Observable<QueryResult<any>> {
        return this.usergroupFactory.getVat(countryAbbreviation);
    }

    getServiceCategories(): Observable<QueryResult<any>> {
        return this.serviceCategoryFactory.getAll();
    }

    getUsergroupHash(): Observable<string> {
        return this.usergroupHash$;
    }

    getUsergroup(): Observable<ApolloQueryResult<UsergroupQuery>> {
        return this.usergroupFactory.getUsergroup({ usergroupHash: this.usergroupHash$ });
    }

    getUsergroupProfileHeaderData(
        usergroupHash: string,
    ): Observable<ApolloQueryResult<UsergroupProfileHeaderDataQuery>> {
        return this.usergroupFactory.getUsergroupProfileHeaderData({ usergroupHash });
    }

    setUsergroupExists(usergroupExists: boolean): void {
        this.usergroupContext.setExists(usergroupExists);
    }

    setUsergroupHash(usergroupHash: string): void {
        this.usergroupContext.setHash(usergroupHash);
    }

    signAgreement(usergroupId: string, userHash: string, licenseId: number): Observable<any> {
        return this.usergroupFactory.signAgreement({ usergroupId, userHash, licenseId });
    }

    storeLabelMutation(variables: StoreLabelMutationVariables): Observable<ApolloLink.Result<StoreLabelMutation>> {
        return this.usergroupFactory.storeLabel(variables);
    }

    // Proforma === Payment Notice
    storePaymentNoticeAndGetUrl(usergroupId: string, amount: number, email: string): Observable<any> {
        return this.usergroupFactory.storeInvoicePaymentAndGetUrl({ usergroupId, amount, email });
    }

    toggleContractsOnUsergroup(
        variables: ToggleContractsOnUsergroupMutationVariables,
    ): Observable<ApolloLink.Result<ToggleContractsOnUsergroupMutation>> {
        return this.usergroupFactory.toggleContractsOnUsergroup(variables);
    }

    updateBankAccount(
        variables: UpdateBankAccountMutationVariables,
    ): Observable<ApolloLink.Result<UpdateBankAccountMutation>> {
        return this.usergroupFactory.updateBankAccount(variables);
    }

    updateHash(hash: string, newHash: string): Observable<any> {
        return this.usergroupFactory.updateUsergroupHash({ hash, newHash });
    }

    usergroupExists(): Observable<boolean> {
        return this.usergroupExists$;
    }

    purchaseRedeemCode(
        variables: PurchaseRedeemCodeMutationVariables,
    ): Observable<ApolloLink.Result<PurchaseRedeemCodeMutation>> {
        return this.usergroupFactory.purchaseRedeemCode(variables);
    }

    purchaseAndSendRedeemCode(
        variables: PurchaseAndSendRedeemCodeMutationVariables,
    ): Observable<ApolloLink.Result<PurchaseAndSendRedeemCodeMutation>> {
        return this.usergroupFactory.purchaseAndSendRedeemCode(variables);
    }

    getRedeemCodes(variables: RedeemCodesQueryVariables): Observable<ApolloQueryResult<RedeemCodesQuery>> {
        return this.newUsergroupFactory.getRedeemCodes(variables);
    }
}
